import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const [state, setState] = useState("checking");
  const [msg, setMsg] = useState("");

  useEffect(() => {
    const token = params.get("token");
    if (!token) { setState("bad"); setMsg("This link is missing its confirmation code."); return; }
    api.post("/auth/verify-email", { token })
      .then(() => {
        setState("ok");
        if (user) setUser({ ...user, email_verified: true });
      })
      .catch((e) => {
        setState("bad");
        setMsg(apiError(e.response?.data?.detail) || "This confirmation link is invalid or has expired.");
      });
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-5">
      <div className="w-full max-w-md">
        <img src="/matiqs-logo.png" alt="Matiqs" className="brand-logo h-8 w-auto mb-8" />
        <div className="bg-card border border-border rounded-2xl p-8">
          {state === "checking" && (
            <div data-testid="verify-checking" className="flex items-center gap-3 text-sm text-muted-foreground py-4">
              <Loader2 className="w-5 h-5 animate-spin text-primary" /> Confirming your email…
            </div>
          )}
          {state === "ok" && (
            <div data-testid="verify-success">
              <CheckCircle2 className="w-9 h-9 text-brand" />
              <h1 className="font-display text-2xl font-bold text-slate-900 dark:text-slate-100 mt-5">Email confirmed</h1>
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                You are all set. Matiqs is your automated quotation platform — free to start, with no
                limit on searching, editing or exporting.
              </p>
              <Button data-testid="verify-continue" onClick={() => navigate(user?.account_type === "contractor" ? "/rfqs" : user?.account_type === "einvoice" ? "/einvoicing/app" : "/dashboard")} className="mt-6 font-semibold">
                {user ? "Continue to Matiqs" : "Sign in"}
              </Button>
            </div>
          )}
          {state === "bad" && (
            <div data-testid="verify-failed">
              <XCircle className="w-9 h-9 text-destructive" />
              <h1 className="font-display text-2xl font-bold text-slate-900 dark:text-slate-100 mt-5">Link not valid</h1>
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{msg}</p>
              <Button variant="outline" onClick={() => navigate(user ? (user.account_type === "einvoice" ? "/einvoicing/app" : user.account_type === "contractor" ? "/marketplace" : "/dashboard") : "/login")} className="mt-6 font-semibold">
                {user ? "Back to Matiqs" : "Sign in"}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
